import { Button } from 'antd';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import { useOscarTheme } from '../../context/OscarRoleContext';
import OscarMark from '../shared/OscarMark';

interface OscarEdgeTabProps {
	onClick: () => void;
	className?: string;
}

/**
 * Four-point sparkle on the tab's corner badge. Inline so it takes the role
 * accent through `currentColor`; antd has no sparkle glyph.
 */
const SparkleGlyph = () => (
	<svg viewBox="0 0 16 16" width={10} height={10} aria-hidden="true" focusable="false">
		<path
			fill="currentColor"
			d="M8 0C8.6 4.2 11.8 7.4 16 8C11.8 8.6 8.6 11.8 8 16C7.4 11.8 4.2 8.6 0 8C4.2 7.4 7.4 4.2 8 0Z"
		/>
	</svg>
);

/**
 * Entry point that opens the Oscar drawer: a tab tucked into the bottom-right
 * edge of the screen on every page, per the "AI Summarization: WO details"
 * prototype. It replaces the launcher pill that sat at the end of the top bar.
 *
 * Half of it sits off-screen until hovered (or focused), then it slides out
 * from the right. On touch screens there is no hover, so the visible half is the
 * tap target.
 *
 * An antd `Button` for focus and keyboard handling, stripped of its own chrome
 * — the tab's look lives on the inner span. `!` because antd's stylesheet loads
 * after Tailwind. Transforms are arbitrary values: preflight is off, so the
 * `translate-*` utilities compile to undefined variables and do nothing.
 */
export const OscarEdgeTab = ({ onClick, className }: OscarEdgeTabProps) => {
	const theme = useOscarTheme();
	return (
		<Button
			type="text"
			aria-label="Open Oscar"
			onClick={onClick}
			className={cn(
				'!fixed bottom-14 right-0 z-[998] !h-auto !border-none !bg-transparent !p-0 !shadow-none',
				'[transform:translateX(28px)] hover:[transform:translateX(0)] focus-visible:[transform:translateX(0)]',
				'!duration-200 motion-reduce:!transition-none',
				className
			)}
		>
			<span
				className={cn(
					// `!flex`: antd's `.ant-btn > span { display: inline-block }` outranks
					// `flex`, which dropped `items-center` and sat the owl on the baseline.
					'relative !flex h-12 w-16 items-center rounded-l-full pl-xs',
					theme.accentSoft,
					theme.stroke,
					'border-r-0'
				)}
			>
				<OscarMark size={OSCAR_MARK_SIZE.launcher} />
				<span
					className={cn(
						'absolute -left-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-white',
						theme.stroke,
						theme.accentText
					)}
				>
					<SparkleGlyph />
				</span>
			</span>
		</Button>
	);
};

export default OscarEdgeTab;
